import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/** Where support enquiries land while a sending domain is being verified. */
export const SUPPORT_INBOX = "quibbliessupport@gmail.com";

const schema = z.object({
  name: z.string().trim().min(1, "Please tell us your name").max(100),
  email: z.string().trim().email("Enter a valid email address").max(255),
  topic: z.string().trim().min(1).max(40),
  message: z
    .string()
    .trim()
    .min(20, "Please give us a little more detail")
    .max(4000, "Please keep your message under 4000 characters"),
  userId: z.string().uuid().nullable().optional(),
});

export const submitSupportMessage = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Light anti-spam: no more than 3 enquiries per address per hour.
    const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const { count } = await supabaseAdmin
      .from("support_messages")
      .select("id", { count: "exact", head: true })
      .eq("email", data.email.toLowerCase())
      .gte("created_at", since);
    if ((count ?? 0) >= 3) {
      throw new Error("You have sent several messages recently — please wait a little while.");
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("support_messages")
      .insert({
        user_id: data.userId ?? null,
        name: data.name,
        email: data.email.toLowerCase(),
        topic: data.topic,
        message: data.message,
      })
      .select("id")
      .maybeSingle();
    if (error) throw new Error(error.message);

    // Signed-in members also get the decision-trail copy in their notifications.
    if (data.userId) {
      const { sendNotice } = await import("@/lib/notices.server");
      await sendNotice(data.userId, "support-received");
    } else {
      try {
        const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
        await sendTemplateEmail("support-confirmation", data.email.toLowerCase(), {
          templateData: { name: data.name, topic: data.topic, message: data.message },
          idempotencyKey: `support-confirmation-${inserted?.id ?? data.email.toLowerCase()}`,
        });
      } catch (mailError) {
        console.error("[support] confirmation email failed", {
          message: mailError instanceof Error ? mailError.message : String(mailError),
        });
      }
    }

    return { ok: true };
  });
